import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Paper } from '@material-ui/core';
import KeyboardArrowRightRoundedIcon from '@material-ui/icons/KeyboardArrowRightRounded';
import { navigate } from '@reach/router';

import { GlobalContext } from '../../../../store';
import { IGame } from '../../../../store/player/games/store';

import { StyledSelectCTPWinner } from './SelectCTPWinner.Styles';
import Button from '../../../../components/Button';
import Scorer from '../Components/Scorer';
import { inchesToFeetAndInches } from '../../../../contrib/Utils/inchesToFeetAndInches';
import { IHoleResult } from '../../../../store/admin/holeResults/store';
import { WinnerSelectorStyled } from './WinnerSelector.Styles';
import ScorerData from '../Components/ScorerData';

interface IProps {
  roundId: number;
  eventId: number;
  holeIndex: number;
}

export const SelectCTPWinner = (props: IProps) => {
  const { eventId, roundId, holeIndex } = props;

  const {
    actions: {
      holeResults: { get: getHoleResults }
    },
    holeResults,
    games,
    loader: { isOpen: loadingData }
  } = useContext(GlobalContext);

  const [game, setGame] = useState<IGame>();
  const [feet, setFeet] = useState<number>();
  const [inches, setInches] = useState<number>();
  const [ctpHoleResult, setctpHoleResult] = useState<IHoleResult>();

  useEffect(() => {
    setGame(Object.values(games).find(g => g.round.id === Number(roundId)));
  }, [games, roundId]);

  useEffect(() => {
    const fetcher = async () => {
      await getHoleResults(roundId, { hole: holeIndex - 1 }, true);
    };

    fetcher().catch();
  }, [roundId, holeIndex]);

  useEffect(() => {
    if (!loadingData) {
      if (holeResults && holeResults[roundId]) {
        const ctpHole = holeResults[roundId].reduce(
          (smallestDistanceHoleResult: IHoleResult | undefined, holeResult) =>
            holeResult.pinDistance &&
            (!smallestDistanceHoleResult ||
              Number(holeResult.pinDistance) < Number(smallestDistanceHoleResult.pinDistance))
              ? holeResult
              : smallestDistanceHoleResult,
          undefined
        );

        if (ctpHole) {
          setctpHoleResult(ctpHole);
        }
      }
    }

    return () => {
      setctpHoleResult(undefined);
    };
  }, [holeResults, roundId, loadingData]);

  useEffect(() => {
    if (ctpHoleResult) {
      const processedDistance = inchesToFeetAndInches(ctpHoleResult.pinDistance);

      if (processedDistance) {
        setFeet(processedDistance.feet);
        setInches(processedDistance.inches);
      }
    }

    return () => {
      setFeet(undefined);
      setInches(undefined);
    };
  }, [ctpHoleResult]);

  const goToScores = useCallback(() => {
    navigate(`/event-scores/${eventId}/round/${roundId}/hole/${holeIndex}/editor`);

    setFeet(undefined);
    setInches(undefined);
    setctpHoleResult(undefined);
  }, [eventId, roundId, holeIndex]);

  const goToSetWinner = useCallback(
    (enrollmentId: number) => () =>
      navigate(
        `/event-scores/${eventId}/round/${roundId}/hole/${holeIndex}/setctpwinner/${enrollmentId}`
      ),
    [eventId, roundId, holeIndex]
  );

  return (
    <StyledSelectCTPWinner className={`score-route set-hole-scores view-scores`}>
      <h2>CLOSEST TO THE PIN CONTEST</h2>

      <Paper elevation={1} className="paper someone-closer-than">
        {(feet || inches) && ctpHoleResult ? (
          <>
            <h3>CURRENT LEADER</h3>

            <ScorerData
              image={ctpHoleResult.userImage}
              firstName={ctpHoleResult.userFirstName}
              lastName={ctpHoleResult.userLastName}
            />

            <div className="distance">
              <div className="number-container">
                <span>{feet}</span>
              </div>

              <span className="unit">FEET</span>

              <div className="number-container">
                <span>{inches}</span>
              </div>

              <span className="unit">INCHES</span>
            </div>
          </>
        ) : (
          <h3>
            No winner yet
            <br />
            Did someone in your
            <br />
            group hit the green?
          </h3>
        )}
      </Paper>

      <div className="enter-scores-container">
        <h3>IS SOMEONE CLOSER?</h3>

        <Button
          classes={{
            label: 'label',
            root: 'enter-scores-button'
          }}
          onClick={goToScores}
        >
          NOPE. ENTER SCORES
          <KeyboardArrowRightRoundedIcon classes={{ root: 'icon' }} />
        </Button>
      </div>

      <WinnerSelectorStyled>
        <p className="title">SELECT WINNER:</p>

        {game &&
          game.group.enrollments.map(e => (
            <Scorer item={e} key={`scorer-${e.id}`} onClick={goToSetWinner(e.id)}>
              <Button
                classes={{
                  label: 'label',
                  root: 'enter-scores-button'
                }}
              >
                <KeyboardArrowRightRoundedIcon classes={{ root: 'icon' }} />
              </Button>
            </Scorer>
          ))}
      </WinnerSelectorStyled>
    </StyledSelectCTPWinner>
  );
};
